import React, { useState, useEffect } from 'react';
import { 
  Users, 
  TrendingUp, 
  AlertTriangle,
  Calendar,
  BarChart3,
  Download,
  Clock,
  CheckCircle2,
  XCircle,
  ClipboardCheck,
  ShieldAlert,
  BookOpen
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { useNavigate } from 'react-router-dom';
import { useSync } from "../../contexts/SyncContext";
import { getCurrentUser } from '@/data/users';
import apiService from '@/services/api';

// Class definitions for Primary Schools (Year 1-6)
const PRIMARY_CLASSES = [
  { id: 'year1', title: 'Year 1', color: 'bg-green-500', year: 1 },
  { id: 'year2', title: 'Year 2', color: 'bg-green-500', year: 2 },
  { id: 'year3', title: 'Year 3', color: 'bg-green-500', year: 3 },
  { id: 'year4', title: 'Year 4', color: 'bg-teal-500', year: 4 },
  { id: 'year5', title: 'Year 5', color: 'bg-teal-500', year: 5 },
  { id: 'year6', title: 'Year 6', color: 'bg-teal-500', year: 6 }
];

// Class definitions for Secondary Schools
const JUNIOR_CLASSES = [
  { id: 'year7a', title: 'Year 7A', color: 'bg-blue-500', year: 7 },
  { id: 'year7b', title: 'Year 7B', color: 'bg-blue-500', year: 7 },
  { id: 'year8', title: 'Year 8', color: 'bg-blue-500', year: 8 },
  { id: 'year9', title: 'Year 9', color: 'bg-blue-500', year: 9 },
  { id: 'year10', title: 'Year 10', color: 'bg-blue-500', year: 10 }
];

const SENIOR_CLASSES = [
  { id: 'year11a', title: 'Year 11A', color: 'bg-purple-500', year: 11 },
  { id: 'year11c', title: 'Year 11C', color: 'bg-purple-500', year: 11 },
  { id: 'year11s', title: 'Year 11S', color: 'bg-purple-500', year: 11 },
  { id: 'year12a', title: 'Year 12A', color: 'bg-purple-500', year: 12 },
  { id: 'year12c', title: 'Year 12C', color: 'bg-purple-500', year: 12 },
  { id: 'year12s', title: 'Year 12S', color: 'bg-purple-500', year: 12 },
  { id: 'year13a', title: 'Year 13A', color: 'bg-purple-500', year: 13 },
  { id: 'year13cs', title: 'Year 13 ICT', color: 'bg-purple-500', year: 13 },
  { id: 'year13s', title: 'Year 13S', color: 'bg-purple-500', year: 13 }
];

export default function DashboardHome() {
  const { getStudentsByClass } = useSync();
  const navigate = useNavigate();
  const currentUser = getCurrentUser();
  
  // Detect if user is from a primary school
  const isPrimarySchool = currentUser?.school_id?.includes('primary') || 
    currentUser?.school_id?.includes('quatui') ||
    currentUser?.school_id?.includes('quirutaro') ||
    currentUser?.school_id?.includes('quatuineala');
  
  const [currentTab, setCurrentTab] = useState(isPrimarySchool ? 'primary' : 'all');
  const [classStats, setClassStats] = useState([]);
  const [overallStats, setOverallStats] = useState({ total: 0, present: 0, absent: 0, late: 0, rate: 0 });
  const [atRiskStudents, setAtRiskStudents] = useState([]);
  const [gradeAnalysisView, setGradeAnalysisView] = useState('weekly');
  const [gradeStats, setGradeStats] = useState({
    excellent: 0,
    good: 0,
    average: 0,
    belowAverage: 0,
    failing: 0
  });
  const [schoolClasses, setSchoolClasses] = useState([]);
  const [schoolName, setSchoolName] = useState('');
  const [studentCount, setStudentCount] = useState(0);
  const [teacherCount, setTeacherCount] = useState(0);
  
  // Timetable state for Master Timetable display (Principal)
  const [timetables, setTimetables] = useState([]);
  const [selectedTimetableClass, setSelectedTimetableClass] = useState('');
  const [currentTimetable, setCurrentTimetable] = useState(null);
  const [timetableLoading, setTimetableLoading] = useState(false);
  
  // Personal timetable for teachers
  const [mySchedule, setMySchedule] = useState(null);

  // Check if user has admin or principal access
  const isAdmin = currentUser?.role === 'admin';
  const isPrincipal = currentUser?.role === 'principal';
  const isDeputyPrincipal = currentUser?.role === 'deputy_principal';
  const isAcademicPrincipal = currentUser?.role === 'academic_principal';
  const isTeacher = currentUser?.role === 'teacher';
  const hasAccess = isAdmin || isPrincipal || isDeputyPrincipal || isAcademicPrincipal;
  
  // Fetch school-specific data
  useEffect(() => {
    const fetchSchoolData = async () => {
      if (!currentUser?.school_id) return;
      
      try {
        const API_URL = '';
        const token = localStorage.getItem('authToken');
        
        // Fetch school info
        const schoolRes = await fetch(`${API_URL}/api/schools/${currentUser.school_id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (schoolRes.ok) {
          const school = await schoolRes.json();
          setSchoolName(school.name || '');
        }
        
        // Fetch classes for this school
        const classesRes = await fetch(`${API_URL}/api/classes/`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (classesRes.ok) {
          const classes = await classesRes.json();
          setSchoolClasses(classes);
        }
        
        // Fetch student count
        const studentsRes = await fetch(`${API_URL}/api/students/`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (studentsRes.ok) {
          const students = await studentsRes.json();
          setStudentCount(students.length);
        }
        
        // Fetch timetables for principals
        if (currentUser?.role === 'principal' || currentUser?.role === 'admin') {
          setTimetableLoading(true);
          try {
            const timetableRes = await fetch(`${API_URL}/api/timetable/school/${currentUser.school_id}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (timetableRes.ok) {
              const timetableData = await timetableRes.json();
              setTimetables(timetableData.timetables || []);
              // Set first class as default
              if (timetableData.timetables && timetableData.timetables.length > 0) {
                setSelectedTimetableClass(timetableData.timetables[0].class_name);
                setCurrentTimetable(timetableData.timetables[0].schedule_data);
              }
            }
          } catch (err) {
            console.error('Error fetching timetables:', err);
          }
          setTimetableLoading(false);
        }
        
        // Fetch personal timetable for teachers
        if (currentUser?.role === 'teacher') {
          setTimetableLoading(true);
          try {
            const myTimetableRes = await fetch(`${API_URL}/api/timetable/my-timetable`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (myTimetableRes.ok) {
              const myTimetableData = await myTimetableRes.json();
              setMySchedule(myTimetableData.schedule);
            }
          } catch (err) {
            console.error('Error fetching personal timetable:', err);
          }
          setTimetableLoading(false);
        }
      } catch (error) {
        console.error('Error fetching school data:', error);
      }
    };
    
    fetchSchoolData();
  }, [currentUser?.school_id, currentUser?.role]);

  // Redirect if not admin or principal
  // Define functions before useEffects
  const calculateStats = () => {
    // For primary schools, use primary classes; for secondary, use junior/senior
    let classList;
    if (isPrimarySchool) {
      classList = PRIMARY_CLASSES;
    } else if (currentTab === 'junior') {
      classList = JUNIOR_CLASSES;
    } else if (currentTab === 'senior') {
      classList = SENIOR_CLASSES;
    } else {
      classList = [...JUNIOR_CLASSES, ...SENIOR_CLASSES];
    }

    let totalStudents = 0;
    let totalPresent = 0;
    let totalAbsent = 0;
    let totalLate = 0;
    let allAtRisk = [];

    const stats = classList.map(cls => {
      const students = getStudentsByClass(cls.title);
      const classTotal = students.length;
      
      // Calculate attendance stats from student data
      let classPresent = 0;
      let classAbsent = 0;
      let classLate = 0;

      students.forEach(student => {
        if (student.attendance) {
          classPresent += student.attendance.present || 0;
          classAbsent += student.attendance.absent || 0;
          classLate += student.attendance.late || 0;

          // Check for at-risk students (4+ absences)
          if (student.attendance.absent >= 4) {
            allAtRisk.push({
              name: student.name,
              class: cls.title,
              absences: student.attendance.absent,
              rate: student.attendance.percentage || 0,
              level: currentTab === 'junior' ? 'Junior' : currentTab === 'senior' ? 'Senior' : 
                     cls.title.includes('Year 7') || cls.title.includes('Year 8') || 
                     cls.title.includes('Year 9') || cls.title.includes('Year 10') ? 'Junior' : 'Senior'
            });
          }
        }
      });

      const totalRecords = classPresent + classAbsent + classLate;
      const attendanceRate = totalRecords > 0 ? Math.round((classPresent / totalRecords) * 100) : 100;

      totalStudents += classTotal;
      totalPresent += classPresent;
      totalAbsent += classAbsent;
      totalLate += classLate;

      return {
        ...cls,
        studentCount: classTotal,
        present: classPresent,
        absent: classAbsent,
        late: classLate,
        rate: attendanceRate,
        level: cls.title.includes('Year 7') || cls.title.includes('Year 8') || 
               cls.title.includes('Year 9') || cls.title.includes('Year 10') ? 'Junior' : 'Senior'
      };
    });

    const overallRecords = totalPresent + totalAbsent + totalLate;
    const overallRate = overallRecords > 0 ? Math.round((totalPresent / overallRecords) * 100) : 100;

    setClassStats(stats);
    setOverallStats({
      total: totalStudents,
      present: totalPresent,
      absent: totalAbsent,
      late: totalLate,
      rate: overallRate
    });
    setAtRiskStudents(allAtRisk.sort((a, b) => b.absences - a.absences));
  };

  const calculateGradeStats = async () => {
    try {
      // Fetch real performance data from the backend
      const data = await apiService.getPerformanceSummary();
      
      setGradeStats({
        excellent: data.performance_stats.excellent || 0,
        good: data.performance_stats.good || 0,
        average: data.performance_stats.average || 0,
        belowAverage: data.performance_stats.belowAverage || 0,
        failing: data.performance_stats.failing || 0
      });

      // Update overall stats with actual student count
      setOverallStats(prev => ({
        ...prev,
        total: data.total_students
      }));

      // Map at-risk students from API response
      const atRisk = data.at_risk_students.map(student => ({
        name: student.student_name,
        class: `Year ${student.year}`,
        percentage: Math.round(student.average_score),
        status: student.average_score < 50 ? 'failing' : 'at-risk'
      }));

      setAtRiskStudents(atRisk);
    } catch (error) {
      console.error('Error fetching performance data:', error);
      // Fall back to zeros on error
      setGradeStats({
        excellent: 0,
        good: 0,
        average: 0,
        belowAverage: 0,
        failing: 0
      });
    }
  };

  const downloadReport = () => {
    let csvContent = "Class,Level,Total Students,Present,Absent,Late,Attendance Rate\n";
    
    classStats.forEach(cls => {
      csvContent += `"${cls.title}","${cls.level}",${cls.studentCount},${cls.present},${cls.absent},${cls.late},${cls.rate}%\n`;
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `Attendance_Overview_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Handle timetable class selection change
  const handleTimetableClassChange = (className) => {
    setSelectedTimetableClass(className);
    const timetable = timetables.find(t => t.class_name === className);
    setCurrentTimetable(timetable?.schedule_data || null);
  };

  // Timetable periods and days
  const TIMETABLE_PERIODS = [
    { number: 1, start: "07:30", end: "08:30" },
    { number: 2, start: "08:30", end: "09:30" },
    { number: 3, start: "10:00", end: "11:00" },
    { number: 4, start: "11:00", end: "12:00" },
    { number: 5, start: "13:00", end: "13:50" },
    { number: 6, start: "13:50", end: "14:40" },
    { number: 7, start: "14:40", end: "15:30" },
  ];
  const TIMETABLE_DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];

  // Render timetable cell (for principal's master timetable)
  const renderTimetableCell = (day, periodNumber) => {
    if (!currentTimetable || !currentTimetable[day]) return null;
    
    const periods = currentTimetable[day];
    const periodData = periods.find(p => {
      if (p.is_double) {
        return periodNumber >= p.period_start && periodNumber <= p.period_end;
      }
      return p.period === periodNumber;
    });
    
    if (!periodData) {
      return <div className="text-xs text-gray-400 text-center">-</div>;
    }
    
    // For double period, only show on first period
    if (periodData.is_double && periodNumber === periodData.period_start) {
      return (
        <div className="p-1 bg-blue-50 border-l-2 border-blue-500 rounded text-xs">
          <div className="font-semibold text-blue-900 truncate">{periodData.subject}</div>
          <div className="text-blue-600 truncate">{periodData.teacher_email?.split('@')[0]}</div>
          <div className="text-blue-500 font-medium">Double</div>
        </div>
      );
    }
    
    if (periodData.is_double && periodNumber > periodData.period_start) {
      return null; // Skip second cell of double period
    }
    
    return (
      <div className="p-1 bg-green-50 border-l-2 border-green-500 rounded text-xs">
        <div className="font-semibold text-green-900 truncate">{periodData.subject}</div>
        <div className="text-green-600 truncate">{periodData.teacher_email?.split('@')[0]}</div>
      </div>
    );
  };

  // Render teacher's personal timetable cell - supports multiple classes per period
  const renderMyScheduleCell = (day, periodNumber) => {
    if (!mySchedule || !mySchedule[day]) return null;
    
    const periods = mySchedule[day];
    // Find ALL matching periods (teacher may have multiple Option classes at same time)
    const matchingPeriods = periods.filter(p => {
      if (p.is_double) {
        return periodNumber >= p.period_start && periodNumber <= p.period_end;
      }
      return p.period === periodNumber;
    });
    
    if (matchingPeriods.length === 0) {
      return <div className="text-xs text-gray-400 text-center">Free</div>;
    }
    
    // Check for double period - skip second cell
    const doublePeriod = matchingPeriods.find(p => p.is_double);
    if (doublePeriod && periodNumber > doublePeriod.period_start) {
      return null;
    }
    
    // Render all classes stacked
    return (
      <div className="space-y-1">
        {matchingPeriods.map((periodData, idx) => {
          const isDouble = periodData.is_double && periodNumber === periodData.period_start;
          return (
            <div key={idx} className="p-1 bg-purple-50 border-l-2 border-purple-500 rounded text-xs">
              <div className="font-semibold text-purple-900 truncate">{periodData.subject}</div>
              <div className="text-purple-600 truncate">{periodData.class_name}</div>
              {isDouble && <div className="text-purple-500 font-medium">Double</div>}
            </div>
          );
        })}
      </div>
    );
  };

  // useEffect hooks
  useEffect(() => {
    if (!hasAccess) {
      navigate('/dashboard');
    }
  }, [hasAccess, navigate]);

  useEffect(() => {
    calculateStats();
    if (isPrincipal) {
      calculateGradeStats();
    }
  // eslint-disable-next-line
  }, [currentTab, isPrimarySchool]);

  // If not admin or principal, show access denied while redirecting
  if (!hasAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <Card className="max-w-md w-full border-2 border-red-200">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-red-100 rounded-full">
                <ShieldAlert className="h-12 w-12 text-red-600" />
              </div>
            </div>
            <CardTitle className="text-2xl text-red-900">Access Denied</CardTitle>
            <CardDescription className="text-base mt-2">
              This page is restricted to Principals and Administrators only.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-sm text-muted-foreground mb-4">
              You don&apos;t have permission to view the Principal&apos;s Analytics Dashboard.
            </p>
            <Button onClick={() => navigate('/dashboard')} className="w-full">
              Return to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Prepare data for 3D Pie Chart
  const pieChartData = [
    { name: 'Present', value: overallStats.present, color: '#16a34a' },
    { name: 'Absent', value: overallStats.absent, color: '#dc2626' },
    { name: 'Late', value: overallStats.late, color: '#f59e0b' }
  ].filter(item => item.value > 0);

  const RADIAN = Math.PI / 180;
  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        className="font-bold text-sm"
      >
        {`${(percent * 100).toFixed(1)}%`}
      </text>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-zinc-50 -m-6 p-6">
      <div className="max-w-7xl mx-auto space-y-6 animate-in fade-in duration-500">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between gap-4 md:items-center">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
              <BarChart3 className="h-8 w-8 text-primary" />
              {isPrimarySchool ? 'Primary School Dashboard' : 'Principal Dashboard'}
            </h1>
            <p className="text-muted-foreground mt-1">
              {isPrimarySchool 
                ? `${schoolName || 'Your School'} - ${studentCount} students enrolled`
                : `Real-time attendance monitoring and analytics for ${schoolName || 'your school'}`
              }
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="text-base py-2 px-4">
              <Calendar className="h-4 w-4 mr-2" />
              {new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
            </Badge>
            {/* Only show Take Roll Call button to Admin, not Principal */}
            {isAdmin && (
              <Button onClick={() => navigate('/dashboard/roll-call')} variant="default">
                <ClipboardCheck className="h-4 w-4 mr-2" />
                Take Roll Call
              </Button>
            )}
            <Button onClick={downloadReport} variant="outline">
              <Download className="h-4 w-4 mr-2" />
              Export Report
            </Button>
          </div>
        </div>

        {/* Overall Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Students</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{overallStats.total}</div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-green-500">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Present</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-600">{overallStats.present}</div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-red-500">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Absent</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-red-600">{overallStats.absent}</div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-amber-500">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Late</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-amber-600">{overallStats.late}</div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-blue-500">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Overall Rate</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-600">{overallStats.rate}%</div>
            </CardContent>
          </Card>
        </div>

        {/* Primary School Quick Overview */}
        {isPrimarySchool && (
          <Card className="border-2 border-green-200 bg-gradient-to-r from-green-50 to-emerald-50">
            <CardHeader>
              <CardTitle className="text-xl flex items-center gap-2 text-green-800">
                🏫 {schoolName || 'Primary School'} Overview
              </CardTitle>
              <CardDescription>Your school at a glance</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-lg p-4 text-center border border-green-200">
                  <div className="text-3xl font-bold text-green-600">{studentCount}</div>
                  <div className="text-sm text-muted-foreground">Total Students</div>
                </div>
                <div className="bg-white rounded-lg p-4 text-center border border-green-200">
                  <div className="text-3xl font-bold text-green-600">{schoolClasses.length}</div>
                  <div className="text-sm text-muted-foreground">Classes</div>
                </div>
                <div className="bg-white rounded-lg p-4 text-center border border-green-200">
                  <div className="text-3xl font-bold text-green-600">6</div>
                  <div className="text-sm text-muted-foreground">Year Levels</div>
                </div>
                <div className="bg-white rounded-lg p-4 text-center border border-green-200">
                  <div className="text-3xl font-bold text-green-600">{Math.round(studentCount / Math.max(schoolClasses.length, 1))}</div>
                  <div className="text-sm text-muted-foreground">Avg Class Size</div>
                </div>
              </div>
              
              {/* Quick Actions for Primary Principal */}
              <div className="mt-6 flex flex-wrap gap-3">
                <Button onClick={() => navigate('/dashboard/students')} variant="outline" className="border-green-300 text-green-700 hover:bg-green-50">
                  <Users className="h-4 w-4 mr-2" />
                  View Student Directory
                </Button>
                <Button onClick={() => navigate('/dashboard/roll-call')} variant="outline" className="border-green-300 text-green-700 hover:bg-green-50">
                  <ClipboardCheck className="h-4 w-4 mr-2" />
                  Daily Roll Call
                </Button>
                <Button onClick={() => navigate('/dashboard/timetable')} variant="outline" className="border-green-300 text-green-700 hover:bg-green-50">
                  <Calendar className="h-4 w-4 mr-2" />
                  View Timetable
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Traffic Light Analysis - Academic Performance */}
        <Card className="border-2 border-slate-200 shadow-lg">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-xl flex items-center gap-2">
                  <span>🚦</span>
                  Traffic Light Analysis - Academic Performance
                </CardTitle>
                <CardDescription className="mt-2">
                  Daily monitoring of student academic performance across all classes
                </CardDescription>
              </div>
              <Badge variant="outline" className="text-sm">
                <Clock className="h-3 w-3 mr-1" />
                Last updated: {new Date().toLocaleTimeString()}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-6 mb-6">
              {/* Green - Good Performance */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-6 text-center hover:shadow-lg transition-shadow">
                <div className="text-6xl mb-4">🟢</div>
                <div className="text-4xl font-bold text-green-700 mb-2">
                  {gradeStats.excellent + gradeStats.good}
                </div>
                <div className="text-lg font-semibold text-green-600 mb-1">Good Performance</div>
                <div className="text-sm text-green-500">70% and above</div>
                <div className="text-xs text-green-400 mt-2">On Track</div>
                <Progress value={((gradeStats.excellent + gradeStats.good) / overallStats.total) * 100} className="mt-4 h-2 bg-green-100" />
              </div>

              {/* Yellow - At Risk */}
              <div className="bg-gradient-to-br from-yellow-50 to-amber-50 border-2 border-yellow-200 rounded-xl p-6 text-center hover:shadow-lg transition-shadow">
                <div className="text-6xl mb-4">🟡</div>
                <div className="text-4xl font-bold text-yellow-700 mb-2">
                  {gradeStats.average}
                </div>
                <div className="text-lg font-semibold text-yellow-600 mb-1">At Risk</div>
                <div className="text-sm text-yellow-500">50-69%</div>
                <div className="text-xs text-yellow-400 mt-2">Needs Support</div>
                <Progress value={(gradeStats.average / overallStats.total) * 100} className="mt-4 h-2 bg-yellow-100" />
              </div>

              {/* Red - Failing */}
              <div className="bg-gradient-to-br from-red-50 to-rose-50 border-2 border-red-200 rounded-xl p-6 text-center hover:shadow-lg transition-shadow">
                <div className="text-6xl mb-4">🔴</div>
                <div className="text-4xl font-bold text-red-700 mb-2">
                  {gradeStats.belowAverage + gradeStats.failing}
                </div>
                <div className="text-lg font-semibold text-red-600 mb-1">Failing</div>
                <div className="text-sm text-red-500">Below 50%</div>
                <div className="text-xs text-red-400 mt-2">Urgent Action Required</div>
                <Progress value={((gradeStats.belowAverage + gradeStats.failing) / overallStats.total) * 100} className="mt-4 h-2 bg-red-100" />
              </div>
            </div>

            {/* Action Items and Quick Stats */}
            <div className="grid grid-cols-2 gap-6">
              {/* Students Requiring Attention */}
              {atRiskStudents.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <h4 className="font-semibold text-amber-900 mb-3 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" />
                    Students Requiring Immediate Attention
                  </h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {atRiskStudents.slice(0, 5).map((student, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-white rounded p-2 text-sm">
                        <div className="flex items-center gap-2">
                          <span className={student.status === 'failing' ? '🔴' : '🟡'}></span>
                          <span className="font-medium text-slate-700">{student.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={student.status === 'failing' ? 'destructive' : 'warning'} className="text-xs">
                            {student.class}
                          </Badge>
                          <span className="text-xs text-slate-500">{student.percentage}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  {atRiskStudents.length > 5 && (
                    <Button 
                      variant="link" 
                      size="sm" 
                      className="mt-2 text-amber-700"
                      onClick={() => navigate('/dashboard/students')}
                    >
                      View all {atRiskStudents.length} students →
                    </Button>
                  )}
                </div>
              )}

              {/* Performance Summary */}
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  Performance Summary
                </h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-600">Excellent (A)</span>
                    <span className="font-bold text-green-600">{gradeStats.excellent} students</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-600">Good (B)</span>
                    <span className="font-bold text-blue-600">{gradeStats.good} students</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-600">Average (C)</span>
                    <span className="font-bold text-yellow-600">{gradeStats.average} students</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-600">Below Average (D)</span>
                    <span className="font-bold text-orange-600">{gradeStats.belowAverage} students</span>
                  </div>
                  <div className="flex justify-between items-center border-t pt-2">
                    <span className="text-sm text-slate-600">Failing (F)</span>
                    <span className="font-bold text-red-600">{gradeStats.failing} students</span>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full mt-4"
                  onClick={() => navigate('/dashboard/gradebook')}
                >
                  View Full Grade Book →
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 3D Pie Chart Visualization */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Attendance Distribution
            </CardTitle>
            <CardDescription>Visual breakdown of overall attendance status</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={pieChartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={renderCustomizedLabel}
                  outerRadius={100}
                  innerRadius={40}
                  fill="#8884d8"
                  dataKey="value"
                  paddingAngle={2}
                >
                  {pieChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value, name) => [`${value.toLocaleString()} records`, name]}
                  contentStyle={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                    border: '1px solid #ccc',
                    borderRadius: '8px',
                    padding: '12px'
                  }}
                />
                <Legend 
                  verticalAlign="bottom" 
                  height={36}
                  iconType="circle"
                />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Master Timetable - Principal Only */}
        {isPrincipal && timetables.length > 0 && (
          <Card className="border-2 border-indigo-200 shadow-lg">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-xl flex items-center gap-2 text-indigo-900">
                    <Calendar className="h-6 w-6" />
                    Master School Timetable
                    <Badge className="bg-indigo-600 ml-2">Principal View</Badge>
                  </CardTitle>
                  <CardDescription className="mt-1">
                    School-wide class schedules • Double Period System
                  </CardDescription>
                </div>
                <Select value={selectedTimetableClass} onValueChange={handleTimetableClassChange}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Select a class" />
                  </SelectTrigger>
                  <SelectContent>
                    {timetables.map((t) => (
                      <SelectItem key={t.id} value={t.class_name}>
                        {t.class_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {timetableLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                </div>
              ) : currentTimetable ? (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-sm">
                    <thead>
                      <tr className="bg-indigo-50">
                        <th className="border border-indigo-200 p-2 text-left font-semibold text-indigo-900">
                          <Clock className="w-4 h-4 inline mr-1" />
                          Period
                        </th>
                        {TIMETABLE_DAYS.map(day => (
                          <th key={day} className="border border-indigo-200 p-2 text-center font-semibold text-indigo-900">
                            {day}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {TIMETABLE_PERIODS.map((period, idx) => (
                        <React.Fragment key={period.number}>
                          {/* Break after period 2 */}
                          {period.number === 3 && (
                            <tr className="bg-amber-50">
                              <td colSpan={6} className="border border-amber-200 p-1 text-center text-xs font-medium text-amber-700">
                                ☕ Break: 9:30 - 10:00
                              </td>
                            </tr>
                          )}
                          {/* Lunch after period 4 */}
                          {period.number === 5 && (
                            <tr className="bg-orange-50">
                              <td colSpan={6} className="border border-orange-200 p-1 text-center text-xs font-medium text-orange-700">
                                🍽️ Lunch: 12:00 - 1:00
                              </td>
                            </tr>
                          )}
                          <tr className="hover:bg-gray-50">
                            <td className="border border-gray-200 p-2 bg-gray-50">
                              <div className="font-medium text-gray-900">P{period.number}</div>
                              <div className="text-xs text-gray-500">{period.start}-{period.end}</div>
                            </td>
                            {TIMETABLE_DAYS.map(day => (
                              <td key={`${day}-${period.number}`} className="border border-gray-200 p-1 min-w-[120px]">
                                {renderTimetableCell(day, period.number)}
                              </td>
                            ))}
                          </tr>
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  Select a class to view timetable
                </div>
              )}
              
              {/* Legend */}
              <div className="mt-4 flex items-center gap-6 text-xs text-gray-600">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-500 rounded"></div>
                  <span>Single Period</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-blue-500 rounded"></div>
                  <span>Double Period</span>
                </div>
              </div>
              
              {/* Quick link to full timetable page */}
              <div className="mt-4 pt-4 border-t">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => navigate('/dashboard/timetable')}
                  className="text-indigo-700 border-indigo-300 hover:bg-indigo-50"
                >
                  <BookOpen className="h-4 w-4 mr-2" />
                  View Full Timetable Page →
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs for filtering - Different for Primary vs Secondary */}
        <Tabs value={currentTab} onValueChange={setCurrentTab}>
          {isPrimarySchool ? (
            // Primary School - Simple single tab view
            <TabsList className="grid w-full max-w-xs grid-cols-1">
              <TabsTrigger 
                value="primary"
                className="data-[state=active]:bg-green-600 data-[state=active]:text-white"
              >
                All Classes (Year 1-6)
              </TabsTrigger>
            </TabsList>
          ) : (
            // Secondary School - Junior/Senior tabs
            <TabsList className="grid w-full max-w-md grid-cols-3">
              <TabsTrigger value="all">All Classes</TabsTrigger>
              <TabsTrigger 
                value="junior" 
                className="data-[state=active]:bg-blue-600 data-[state=active]:text-white"
              >
                Junior
              </TabsTrigger>
              <TabsTrigger 
                value="senior"
                className="data-[state=active]:bg-purple-600 data-[state=active]:text-white"
              >
                Senior
              </TabsTrigger>
            </TabsList>
          )}

          <TabsContent value={currentTab} className="space-y-6 mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Class Statistics Table */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle>Class-by-Class Breakdown</CardTitle>
                  <CardDescription>Attendance statistics for each class</CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Class</TableHead>
                        <TableHead className="text-center">Students</TableHead>
                        <TableHead className="text-center">Present</TableHead>
                        <TableHead className="text-center">Absent</TableHead>
                        <TableHead className="text-center">Late</TableHead>
                        <TableHead className="text-right">Rate</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {classStats.map(cls => (
                        <TableRow key={cls.id}>
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              <div className={`h-3 w-3 rounded-full ${
                                isPrimarySchool ? 'bg-green-500' :
                                cls.level === 'Junior' ? 'bg-blue-500' : 'bg-purple-500'
                              }`} />
                              {cls.title}
                            </div>
                          </TableCell>
                          <TableCell className="text-center">{cls.studentCount}</TableCell>
                          <TableCell className="text-center">
                            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                              {cls.present}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                              {cls.absent}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                              {cls.late}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center gap-2 justify-end">
                              <Progress value={cls.rate} className="w-16 h-2" />
                              <span className="font-semibold text-sm">{cls.rate}%</span>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* Student Grade Analysis - Principal Only */}
              {isPrincipal && (
                <Card className="lg:col-span-2 border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2 text-amber-900">
                          <BarChart3 className="h-5 w-5" />
                          Student Grade Performance Analysis
                          <Badge className="bg-amber-600 ml-2">Principal View</Badge>
                        </CardTitle>
                        <CardDescription>Academic performance distribution across all students</CardDescription>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant={gradeAnalysisView === 'daily' ? 'default' : 'outline'}
                          onClick={() => setGradeAnalysisView('daily')}
                          className="text-xs"
                        >
                          Daily
                        </Button>
                        <Button
                          size="sm"
                          variant={gradeAnalysisView === 'weekly' ? 'default' : 'outline'}
                          onClick={() => setGradeAnalysisView('weekly')}
                          className="text-xs"
                        >
                          Weekly
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Pie Chart */}
                      <div className="flex flex-col items-center">
                        <h4 className="text-sm font-semibold mb-4 text-amber-900">
                          {gradeAnalysisView === 'daily' ? 'Today\'s' : 'This Week\'s'} Performance Distribution
                        </h4>
                        <ResponsiveContainer width="100%" height={250}>
                          <PieChart>
                            <Pie
                              data={[
                                { name: 'Excellent (80-100%)', value: gradeStats.excellent, color: '#10b981' },
                                { name: 'Good (70-79%)', value: gradeStats.good, color: '#3b82f6' },
                                { name: 'Average (60-69%)', value: gradeStats.average, color: '#f59e0b' },
                                { name: 'Below Average (50-59%)', value: gradeStats.belowAverage, color: '#f97316' },
                                { name: 'Failing (<50%)', value: gradeStats.failing, color: '#ef4444' }
                              ]}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                              outerRadius={90}
                              fill="#8884d8"
                              dataKey="value"
                            >
                              {[
                                { color: '#10b981' },
                                { color: '#3b82f6' },
                                { color: '#f59e0b' },
                                { color: '#f97316' },
                                { color: '#ef4444' }
                              ].map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                              ))}
                            </Pie>
                            <Tooltip 
                              contentStyle={{ 
                                backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                                border: '1px solid #e5e7eb',
                                borderRadius: '8px',
                                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                              }}
                            />
                            <Legend 
                              verticalAlign="bottom" 
                              height={36}
                              iconType="circle"
                              wrapperStyle={{ fontSize: '12px' }}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>

                      {/* Statistics Cards */}
                      <div className="space-y-3">
                        <h4 className="text-sm font-semibold mb-4 text-amber-900">Detailed Breakdown</h4>
                        
                        {/* Excellent */}
                        <div className="bg-white rounded-lg p-4 border-l-4 border-green-500 shadow-sm">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-xs text-gray-600 mb-1">Excellent (80-100%)</p>
                              <p className="text-2xl font-bold text-green-600">{gradeStats.excellent}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-xs text-gray-500">
                                {((gradeStats.excellent / (gradeStats.excellent + gradeStats.good + gradeStats.average + gradeStats.belowAverage + gradeStats.failing || 1)) * 100).toFixed(1)}%
                              </p>
                              <Badge className="bg-green-100 text-green-800 text-xs mt-1">Outstanding</Badge>
                            </div>
                          </div>
                          <Progress value={(gradeStats.excellent / (gradeStats.excellent + gradeStats.good + gradeStats.average + gradeStats.belowAverage + gradeStats.failing || 1)) * 100} className="mt-2 h-2 bg-green-100" />
                        </div>

                        {/* Good */}
                        <div className="bg-white rounded-lg p-4 border-l-4 border-blue-500 shadow-sm">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-xs text-gray-600 mb-1">Good (70-79%)</p>
                              <p className="text-2xl font-bold text-blue-600">{gradeStats.good}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-xs text-gray-500">
                                {((gradeStats.good / (gradeStats.excellent + gradeStats.good + gradeStats.average + gradeStats.belowAverage + gradeStats.failing || 1)) * 100).toFixed(1)}%
                              </p>
                              <Badge className="bg-blue-100 text-blue-800 text-xs mt-1">Strong</Badge>
                            </div>
                          </div>
                          <Progress value={(gradeStats.good / (gradeStats.excellent + gradeStats.good + gradeStats.average + gradeStats.belowAverage + gradeStats.failing || 1)) * 100} className="mt-2 h-2 bg-blue-100" />
                        </div>

                        {/* Average */}
                        <div className="bg-white rounded-lg p-4 border-l-4 border-amber-500 shadow-sm">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-xs text-gray-600 mb-1">Average (60-69%)</p>
                              <p className="text-2xl font-bold text-amber-600">{gradeStats.average}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-xs text-gray-500">
                                {((gradeStats.average / (gradeStats.excellent + gradeStats.good + gradeStats.average + gradeStats.belowAverage + gradeStats.failing || 1)) * 100).toFixed(1)}%
                              </p>
                              <Badge className="bg-amber-100 text-amber-800 text-xs mt-1">Satisfactory</Badge>
                            </div>
                          </div>
                          <Progress value={(gradeStats.average / (gradeStats.excellent + gradeStats.good + gradeStats.average + gradeStats.belowAverage + gradeStats.failing || 1)) * 100} className="mt-2 h-2 bg-amber-100" />
                        </div>

                        {/* Below Average */}
                        <div className="bg-white rounded-lg p-4 border-l-4 border-orange-500 shadow-sm">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-xs text-gray-600 mb-1">Below Average (50-59%)</p>
                              <p className="text-2xl font-bold text-orange-600">{gradeStats.belowAverage}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-xs text-gray-500">
                                {((gradeStats.belowAverage / (gradeStats.excellent + gradeStats.good + gradeStats.average + gradeStats.belowAverage + gradeStats.failing || 1)) * 100).toFixed(1)}%
                              </p>
                              <Badge className="bg-orange-100 text-orange-800 text-xs mt-1">At Risk</Badge>
                            </div>
                          </div>
                          <Progress value={(gradeStats.belowAverage / (gradeStats.excellent + gradeStats.good + gradeStats.average + gradeStats.belowAverage + gradeStats.failing || 1)) * 100} className="mt-2 h-2 bg-orange-100" />
                        </div>

                        {/* Failing */}
                        <div className="bg-white rounded-lg p-4 border-l-4 border-red-500 shadow-sm">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-xs text-gray-600 mb-1">Failing (&lt;50%)</p>
                              <p className="text-2xl font-bold text-red-600">{gradeStats.failing}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-xs text-gray-500">
                                {((gradeStats.failing / (gradeStats.excellent + gradeStats.good + gradeStats.average + gradeStats.belowAverage + gradeStats.failing || 1)) * 100).toFixed(1)}%
                              </p>
                              <Badge className="bg-red-100 text-red-800 text-xs mt-1">Critical</Badge>
                            </div>
                          </div>
                          <Progress value={(gradeStats.failing / (gradeStats.excellent + gradeStats.good + gradeStats.average + gradeStats.belowAverage + gradeStats.failing || 1)) * 100} className="mt-2 h-2 bg-red-100" />
                        </div>
                      </div>
                    </div>

                    {/* Summary Note */}
                    <div className="mt-6 p-4 bg-amber-100 border border-amber-300 rounded-lg">
                      <p className="text-sm text-amber-900">
                        <strong>📊 Analysis Period:</strong> {gradeAnalysisView === 'daily' ? 'Today\'s performance metrics' : 'This week\'s cumulative performance'}
                        <br />
                        <strong>📈 Total Students Analyzed:</strong> {gradeStats.excellent + gradeStats.good + gradeStats.average + gradeStats.belowAverage + gradeStats.failing}
                        <br />
                        <strong>✨ Success Rate (≥60%):</strong> {(((gradeStats.excellent + gradeStats.good + gradeStats.average) / (gradeStats.excellent + gradeStats.good + gradeStats.average + gradeStats.belowAverage + gradeStats.failing || 1)) * 100).toFixed(1)}%
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* At-Risk Students */}
              <Card className="lg:col-span-1">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-red-600">
                    <AlertTriangle className="h-5 w-5" />
                    At-Risk Students
                  </CardTitle>
                  <CardDescription>Students with 4+ absences</CardDescription>
                </CardHeader>
                <CardContent>
                  {atRiskStudents.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <CheckCircle2 className="h-12 w-12 mx-auto mb-2 text-green-500" />
                      <p className="text-sm">No at-risk students</p>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {atRiskStudents.map((student, idx) => (
                        <Card key={idx} className="border-l-4 border-l-red-500">
                          <CardContent className="pt-4 pb-4">
                            <div className="flex justify-between items-start mb-2">
                              <div>
                                <p className="font-semibold">{student.name}</p>
                                <p className="text-xs text-muted-foreground">{student.class}</p>
                              </div>
                              <Badge 
                                variant="outline" 
                                className={`${student.level === 'Junior' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-purple-50 text-purple-700 border-purple-200'}`}
                              >
                                {student.level}
                              </Badge>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-red-600 font-semibold flex items-center gap-1">
                                <XCircle className="h-4 w-4" />
                                {student.absences} absences
                              </span>
                              <span className="text-muted-foreground">{student.rate}% rate</span>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
